/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Interactive Language Switcher Dropdown (Task 10.7.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useRef, useEffect } from 'react';
import { useI18nStore, SUPPORTED_LOCALES, LOCALE_CONFIG, type SupportedLocale } from '../../i18n';
import { Globe, Check, ChevronDown } from 'lucide-react';

export interface LanguageSwitcherProps {
  variant?: 'header' | 'compact' | 'footer';
  className?: string;
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  variant = 'header',
  className = '',
}) => {
  const { currentLocale, setLocale, getMetadata } = useI18nStore();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const activeMeta = getMetadata();

  // Close dropdown on outside click or Escape key
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleSelectLocale = (locale: SupportedLocale) => {
    setLocale(locale);
    setIsOpen(false);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl border border-surface-border bg-surface hover:bg-surface-elevated text-foreground text-xs font-medium transition-all shadow-sm ${
          isOpen ? 'ring-2 ring-primary/30 border-primary' : ''
        }`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        title={`Change language: Current is ${activeMeta.name}`}
      >
        <span className="text-sm select-none" aria-hidden="true">
          {activeMeta.flag}
        </span>
        {variant !== 'compact' && (
          <span className="font-semibold text-xs tracking-wide uppercase font-mono">
            {activeMeta.code}
          </span>
        )}
        <ChevronDown
          className={`w-3 h-3 text-foreground-muted transition-transform duration-200 ${
            isOpen ? 'rotate-180 text-primary' : ''
          }`}
        />
      </button>

      {/* Popover Dropdown Panel */}
      {isOpen && (
        <div
          role="listbox"
          aria-label="Supported Languages"
          className="absolute right-0 mt-2 w-52 rounded-2xl bg-surface border border-surface-border shadow-2xl p-1.5 z-50 space-y-1 animate-fade-in text-xs backdrop-blur-xl"
        >
          {/* Header Label */}
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 border-b border-surface-border/60 text-[10px] font-semibold text-foreground-muted uppercase tracking-wider">
            <Globe className="w-3 h-3 text-primary" />
            <span>Select Language</span>
          </div>

          {/* Locales List */}
          <div className="space-y-0.5 max-h-64 overflow-y-auto py-0.5">
            {SUPPORTED_LOCALES.map((localeCode) => {
              const meta = LOCALE_CONFIG[localeCode];
              const isSelected = currentLocale === localeCode;

              return (
                <button
                  key={localeCode}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => handleSelectLocale(localeCode)}
                  className={`w-full flex items-center justify-between px-2.5 py-2 rounded-xl text-left transition-colors ${
                    isSelected
                      ? 'bg-primary/10 text-primary font-semibold'
                      : 'text-foreground hover:bg-muted/70 hover:text-foreground'
                  }`}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className="text-base select-none leading-none">{meta.flag}</span>
                    <div className="flex flex-col truncate">
                      <span className="truncate leading-tight font-medium">{meta.nativeName}</span>
                      <span className="text-[10px] text-foreground-muted truncate">
                        {meta.name}
                      </span>
                    </div>
                  </div>

                  {isSelected && (
                    <Check className="w-3.5 h-3.5 text-primary flex-shrink-0 ml-2" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
